import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ValueItemType } from '@app/core/models/common.model';
import { ReservationValueDetailsModel } from '../reservation-values.model';
import { newClientUid } from '@app/shared/utilities/client-uid.util';

interface ReservationItemForm {
  itemType: FormControl<number>;
  code: FormControl<string>;
  amountWithoutTax: FormControl<number | null>;
  taxRate: FormControl<number | null>;
  excess: FormControl<number | null>;
  isByRental: FormControl<boolean>;
  isIncluded: FormControl<boolean>;
  isSoldByBroker: FormControl<boolean>;
  isAccepted: FormControl<boolean>;
}

export type ReservationValuesUpsertDialogComponentConfig =
  // Create picks from the reservation's available (calculated-data) items; the chosen one is added
  // as accepted by default.
  | { mode: 'create'; availableItems: ReservationValueDetailsModel[] }
  | { mode: 'update'; item: ReservationValueDetailsModel; acceptOnly: boolean }
  | { mode: 'readonly'; item: ReservationValueDetailsModel }
  | null;

@Component({
  standalone: true,
  selector: 'app-reservation-values-upsert-dialog',
  templateUrl: './reservation-values-upsert-dialog.component.html',
  imports: [ReactiveFormsModule],
  host: { class: 'd-flex flex-column flex-fill' }
})
export class ReservationValuesUpsertDialogComponent implements OnInit {
  private _formBuilder = inject(NonNullableFormBuilder);
  private _modal = inject(NgbActiveModal);

  config = signal<ReservationValuesUpsertDialogComponentConfig>(null);
  errors: string[] | null = null;

  isReadonly = computed(() => this.config()?.mode === 'readonly');
  isCreate = computed(() => this.config()?.mode === 'create');
  availableItems = computed(() => {
    const config = this.config();
    return config?.mode === 'create' ? config.availableItems : [];
  });

  form: FormGroup<ReservationItemForm>;

  constructor() {
    this.form = this._formBuilder.group<ReservationItemForm>({
      itemType: this._formBuilder.control(0, [Validators.required]),
      code: this._formBuilder.control('', [Validators.required]),
      amountWithoutTax: this._formBuilder.control(null, [Validators.required]),
      taxRate: this._formBuilder.control(null, [Validators.required]),
      excess: this._formBuilder.control(null, [Validators.required]),
      isByRental: this._formBuilder.control(false, [Validators.required]),
      isIncluded: this._formBuilder.control(false, [Validators.required]),
      isSoldByBroker: this._formBuilder.control(false, [Validators.required]),
      isAccepted: this._formBuilder.control(false, [Validators.required])
    });

    // Create mode: picking a code fills every other field from the matching catalog item.
    this.form.controls.code.valueChanges.subscribe((code) => {
      if (this.config()?.mode !== 'create') return;

      const item = this.availableItems().find((x) => x.code === code);
      if (!item) return;

      this.form.patchValue({
        itemType: item.itemType,
        amountWithoutTax: item.amountWithoutTax,
        taxRate: item.taxRate,
        excess: item.excess,
        isByRental: item.isByRental,
        isIncluded: item.isIncluded,
        isSoldByBroker: item.isSoldByBroker
      });
    });
  }

  ngOnInit(): void {
    this.form.markAllAsTouched();
    const config = this.config();
    if (!config) return;

    if (config.mode === 'create') {
      // Added items are accepted by default; the switch is hidden in create mode. Everything but
      // the code selector is derived from the chosen catalog item, so keep those fields read-only.
      this.form.controls.isAccepted.setValue(true);
      this.form.controls.itemType.disable();
      this.form.controls.amountWithoutTax.disable();
      this.form.controls.taxRate.disable();
      this.form.controls.excess.disable();
      this.form.controls.isByRental.disable();
      this.form.controls.isIncluded.disable();
      this.form.controls.isSoldByBroker.disable();
      this.form.controls.isAccepted.disable();
      return;
    }

    this.initialize(config.item);
  }

  submit() {
    this.errors = null;
    if (this.form.invalid) return;

    this._modal.close(this.createModelValues());
  }

  dismiss() {
    this._modal.dismiss();
  }

  getItemTypeOptions() {
    return Object.values(ValueItemType)
      .filter((x) => !isNaN(x as number))
      .map((x) => ({ value: x, name: ValueItemType[x as number] }));
  }

  private initialize(item: ReservationValueDetailsModel) {
    this.form.setValue({
      itemType: item.itemType,
      code: item.code.toUpperCase(),
      amountWithoutTax: item.amountWithoutTax,
      taxRate: item.taxRate,
      excess: item.excess,
      isByRental: item.isByRental,
      isIncluded: item.isIncluded,
      isSoldByBroker: item.isSoldByBroker,
      isAccepted: item.isAccepted
    });

    this.form.markAsUntouched();

    const config = this.config();
    if (config?.mode === 'readonly') {
      this.form.disable();
    } else if (config?.mode === 'update' && config.acceptOnly) {
      // Only the acceptance toggle is editable; every other field comes from the booking service.
      this.form.disable();
      this.form.controls.isAccepted.enable();
    }
  }

  private createModelValues() {
    const formData = this.form.getRawValue();
    const config = this.config();
    const isCreate = config?.mode === 'create';

    return {
      ...formData,
      itemType: Number(formData.itemType),
      isAccepted: isCreate ? true : formData.isAccepted,
      uid: config && config.mode !== 'create' ? config.item.uid : newClientUid()
    };
  }
}
